import moment from 'moment';

/**
 * A string that changes whenever the required recurring charges change: one
 * added, removed, or with a new amount. The order they arrive in does not
 * matter. Used as an effect dependency, because the charges array itself is
 * rebuilt on every render.
 *
 * @param {Array} monthlyRecurringCharges - Recurring required charges
 */
export const getRequiredChargesSignature = (monthlyRecurringCharges) =>
  JSON.stringify(
    monthlyRecurringCharges
      .map((charge) =>
        JSON.stringify([
          charge.id ?? charge.label,
          charge.value,
          charge.descriptiveValue,
          Boolean(charge.isRemoved),
        ]),
      )
      .sort(),
  );

/**
 * Computes the total base "other" monthly amount used as the input for
 * proration. Includes non-rent recurring required charges, monthly pet rent
 * (multiplied by petCount when at least one pet is present), and short term
 * fee (only when isShortTermLease is true). Removed charges are excluded.
 *
 * @param {Object}  params
 * @param {Array}   params.monthlyRecurringCharges - Active recurring charges
 * @param {*}       params.monthlyPetRent - Per-pet monthly rent (raw form value)
 * @param {*}       params.shortTermFee - Short term fee (raw form value)
 * @param {number}  params.petCount - Number of active pets (0 = no pets, omit pet rent)
 * @param {boolean} params.isShortTermLease - Whether the selected lease term qualifies as short-term
 */
export const getBaseOtherAmount = ({
  monthlyRecurringCharges,
  monthlyPetRent,
  shortTermFee,
  petCount = 0,
  isShortTermLease = false,
}) => {
  const nonRentAmountsTotal = monthlyRecurringCharges
    .filter((charge) => !charge.isRemoved)
    .reduce((total, charge) => {
      const amount = parseFloat(charge.value);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);

  const monthlyPetRentAmount = parseFloat(monthlyPetRent);
  const shortTermFeeAmount = parseFloat(shortTermFee);

  const totalPetRent =
    petCount >= 1 && !isNaN(monthlyPetRentAmount)
      ? monthlyPetRentAmount * petCount
      : 0;

  const totalShortTermFee =
    isShortTermLease && !isNaN(shortTermFeeAmount) ? shortTermFeeAmount : 0;

  return nonRentAmountsTotal + totalPetRent + totalShortTermFee;
};

/**
 * Calculates the prorated amount for household rentable items within the lease
 * start month. Items with their own date windows are prorated only for the days
 * they overlap with [leaseStartDate, end of lease start month].
 */
export const getRentableItemsProratedAmount = (
  householdRentableItems,
  leaseStartDate,
) => {
  const leaseStart = moment(leaseStartDate).startOf('day');
  if (!leaseStart.isValid()) {
    return 0;
  }

  const leaseStartMonthEnd = leaseStart.clone().endOf('month').startOf('day');
  const daysInLeaseStartMonth = leaseStart.daysInMonth();

  return householdRentableItems.reduce((total, item) => {
    const recurringAmount = parseFloat(
      item?.propertyRentableItem?.propertyFee?.feeAmount,
    );
    const itemStartDate = moment(item?.startDate).startOf('day');
    const itemEndDate = item?.endDate
      ? moment(item?.endDate).startOf('day')
      : leaseStartMonthEnd;

    if (
      isNaN(recurringAmount) ||
      recurringAmount <= 0 ||
      !itemStartDate.isValid() ||
      !itemEndDate.isValid()
    ) {
      return total;
    }

    const prorateStartDate = moment.max(itemStartDate, leaseStart);
    const prorateEndDate = moment.min(itemEndDate, leaseStartMonthEnd);
    if (prorateEndDate.isBefore(prorateStartDate, 'day')) {
      return total;
    }

    const activeDays = prorateEndDate.diff(prorateStartDate, 'days') + 1;
    return total + (recurringAmount * activeDays) / daysInLeaseStartMonth;
  }, 0);
};

/**
 * Recomputes `proratedRent` and `proratedOther` from a new lease rent amount — the same
 * downstream recalculation `onChangeLeaseRentAmount` (MonthlyLeaseCharges/index.js) performs when
 * the user types a new amount into the Lease Rent field directly.
 *
 * ENG-30333 / Finding 7: `useRecommendedLeaseRent`'s `onRecalculate` used to write `leasedRent`
 * straight through redux-form's `change` action, which does NOT invoke the <Field>'s `onChange`
 * handler — the only place this recomputation normally happens. That left `proratedRent` derived
 * from the PREVIOUS rent after an automatic recalculation, so the resident's first-month proration
 * silently disagreed with the rent actually on the lease. Both call sites (a manual edit and an
 * automatic recalculation) must funnel through this one function so they can never diverge again.
 */
export const recalculateProratedAmountsForRentChange = ({
  rentAmount,
  startDate,
  monthlyRecurringCharges,
  formValues,
  petCount,
  isShortTermLease,
  householdRentableItems,
  setProratedRentAmount,
  setProratedOtherAmount,
  resetProratedRentAutoFilledChangeStatus,
  change,
}) => {
  const baseOtherAmount = getBaseOtherAmount({
    monthlyRecurringCharges,
    monthlyPetRent: formValues?.monthlyPetRent,
    shortTermFee: formValues?.shortTermFee,
    petCount,
    isShortTermLease,
  });
  const rentableItemsProratedAmount = getRentableItemsProratedAmount(
    householdRentableItems,
    startDate,
  );

  setProratedRentAmount({
    startDate: moment(startDate),
    rentAmount,
    updateFn: (val) => {
      change('proratedRent', val);
      resetProratedRentAutoFilledChangeStatus();
    },
  });
  setProratedOtherAmount({
    startDate: moment(startDate),
    otherAmount: baseOtherAmount,
    updateFn: (val) => {
      const baseProratedOtherAmount = parseFloat(val);
      const safeBaseAmount = isNaN(baseProratedOtherAmount)
        ? 0
        : baseProratedOtherAmount;
      const finalProratedOtherAmount = (
        safeBaseAmount + rentableItemsProratedAmount
      ).toFixed(2);
      change('proratedOther', finalProratedOtherAmount);
      resetProratedRentAutoFilledChangeStatus();
    },
  });
};
